import { useEffect, useRef, useState, type FormEvent } from 'react';
import { ChevronRight, Loader2, Trash2, X } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogTitle,
  AlertDialogTrigger,
} from './ui/alert-dialog';
import { normalizeAccountEmail } from '../services/authService';

interface DeleteAccountConfirmProps {
  open: boolean;
  email: string;
  onConfirm: (confirmEmail: string) => Promise<void>;
  onOpenChange: (open: boolean) => void;
}

export function DeleteAccountConfirm({
  open,
  email,
  onConfirm,
  onOpenChange,
}: DeleteAccountConfirmProps) {
  const [confirmation, setConfirmation] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const submittingRef = useRef(false);
  const canConfirm =
    normalizeAccountEmail(confirmation) === normalizeAccountEmail(email) &&
    normalizeAccountEmail(email).length > 0;

  useEffect(() => {
    if (!open) {
      setConfirmation('');
      setError(null);
    }
  }, [open]);

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen && submittingRef.current) return;
    onOpenChange(nextOpen);
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (!canConfirm || submittingRef.current) return;

    submittingRef.current = true;
    setIsSubmitting(true);
    setError(null);
    try {
      await onConfirm(normalizeAccountEmail(confirmation));
    } catch {
      setError('We could not delete your account. Your account is still active. Please try again.');
    } finally {
      submittingRef.current = false;
      setIsSubmitting(false);
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={handleOpenChange}>
      <AlertDialogTrigger asChild>
        <button
          type="button"
          className="w-full flex items-center gap-3 px-4 py-3.5 transition-colors active:opacity-70"
        >
          <div
            className="size-9 rounded-xl flex items-center justify-center shrink-0"
            style={{ backgroundColor: '#ef444420' }}
          >
            <Trash2 className="w-4 h-4" style={{ color: '#ef4444' }} />
          </div>
          <div className="flex-1 text-left min-w-0">
            <p className="text-sm" style={{ color: 'var(--app-text)' }}>Delete Account</p>
            <p className="text-xs mt-0.5" style={{ color: 'var(--app-text-muted)' }}>
              Permanently delete your account and personal data
            </p>
          </div>
          <ChevronRight className="w-4 h-4 shrink-0" style={{ color: 'var(--app-text-subtle)' }} />
        </button>
      </AlertDialogTrigger>

      <AlertDialogContent
        className="max-w-sm gap-0 rounded-2xl border p-0 overflow-hidden"
        style={{ backgroundColor: 'var(--app-surface)', borderColor: 'var(--app-danger-border)' }}
        onOpenAutoFocus={(event) => {
          event.preventDefault();
          inputRef.current?.focus();
        }}
        onEscapeKeyDown={(event) => {
          if (submittingRef.current) event.preventDefault();
        }}
      >
        <form onSubmit={handleSubmit}>
          <div className="flex items-start justify-between gap-3 p-5">
            <div
              className="size-12 rounded-full flex items-center justify-center shrink-0"
              style={{ backgroundColor: 'var(--app-danger-dim)' }}
            >
              <Trash2 className="w-5 h-5" style={{ color: 'var(--app-danger)' }} />
            </div>
            <AlertDialogCancel asChild>
              <button
                type="button"
                disabled={isSubmitting}
                aria-label="Close account deletion confirmation"
                className="size-9 rounded-full flex items-center justify-center disabled:opacity-40"
                style={{ backgroundColor: 'var(--app-muted)' }}
              >
                <X className="w-4 h-4" style={{ color: 'var(--app-text)' }} />
              </button>
            </AlertDialogCancel>
          </div>

          <div className="px-5 pb-5">
            <AlertDialogTitle className="text-base" style={{ color: 'var(--app-danger)' }}>
              Permanently delete your account?
            </AlertDialogTitle>
            <AlertDialogDescription
              className="text-xs mt-2 leading-relaxed"
              style={{ color: 'var(--app-text-muted)' }}
            >
              This permanently removes your profile, registrations, notes, favorites, messages,
              AI history, and personalization. This action cannot be undone.
            </AlertDialogDescription>

            <label
              htmlFor="delete-account-email"
              className="block text-xs mt-5 mb-2"
              style={{ color: 'var(--app-text)' }}
            >
              Type <span className="font-medium">{email}</span> to confirm
            </label>
            <input
              ref={inputRef}
              id="delete-account-email"
              type="email"
              inputMode="email"
              autoCapitalize="none"
              autoComplete="off"
              spellCheck={false}
              value={confirmation}
              onChange={(event) => setConfirmation(event.target.value)}
              disabled={isSubmitting}
              aria-invalid={error ? true : undefined}
              aria-describedby={error ? 'delete-account-error' : undefined}
              className="w-full h-11 rounded-xl border px-3 text-sm outline-none disabled:opacity-60"
              style={{
                backgroundColor: 'var(--app-input-bg)',
                borderColor: error ? 'var(--app-danger)' : 'var(--app-input-border)',
                color: 'var(--app-text)',
              }}
            />

            {error && (
              <p
                id="delete-account-error"
                role="alert"
                className="text-xs mt-2 leading-relaxed"
                style={{ color: 'var(--app-danger)' }}
              >
                {error}
              </p>
            )}
          </div>

          <div className="flex gap-3 px-5 pb-5">
            <AlertDialogCancel asChild>
              <button
                type="button"
                disabled={isSubmitting}
                className="flex-1 h-11 rounded-xl border text-sm disabled:opacity-40"
                style={{ borderColor: 'var(--app-border)', color: 'var(--app-text-muted)' }}
              >
                Cancel
              </button>
            </AlertDialogCancel>
            <button
              type="submit"
              disabled={!canConfirm || isSubmitting}
              aria-busy={isSubmitting}
              className="flex-[1.4] h-11 rounded-xl text-sm flex items-center justify-center gap-2 disabled:opacity-40"
              style={{ backgroundColor: 'var(--app-danger)', color: '#fff' }}
            >
              {isSubmitting && <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />}
              {isSubmitting ? 'Deleting Account…' : 'Permanently Delete Account'}
            </button>
          </div>
        </form>
      </AlertDialogContent>
    </AlertDialog>
  );
}
